/**
 * ROSEYS Cinematic Loading Orchestrator
 *
 * 2026 — Dark theater. The show begins.
 * - #141414 darkness IS the loading state
 * - Hero fades in immediately (0.8s)
 * - Scroll-triggered reveals with upward drift
 * - All sections up to and including K/U exempt — show immediately
 * - No spinners, no indicators, ever
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    heroLoadTimeout: 4000,      // Max wait for hero (4s)
    sectionLoadTimeout: 1500,   // Max wait per section (1.5s)
    belowKuSectionLoadTimeout: 4000, // Longer wait for sections below K/U (4s)
    revealDelay: 50,            // Minimal delay between reveals for smooth cascade
    scrollThreshold: 0.1        // 10% of section visible to trigger
  };

  // State
  const state = {
    heroReady: false,
    sectionsLoaded: new Set(),
    sectionsInView: new Set(),
    revealQueue: [],
    isProcessingQueue: false
  };

  /**
   * Initialize the loading orchestrator
   */
  function init() {
    // Add loading-active class to body
    document.body.classList.add('loading-active');

    // Get all main content sections
    const sections = getSections();

    // Find the K/U section index — everything up to and including it shows immediately
    var kuIndex = -1;
    sections.forEach(function(s, i) {
      if (s.id && s.id.indexOf('KnUnkn') !== -1) kuIndex = i;
    });
    // Fallback: if K/U not found, exempt at least the first 6 sections
    if (kuIndex < 0) kuIndex = 5;

    // Initialize each section
    sections.forEach((section, index) => {
      section.dataset.loadingIndex = index;

      // All sections up to and including K/U: show immediately (no orchestrator delay)
      if (index <= kuIndex) {
        section.classList.remove('section-loading', 'section-queued');
        section.classList.add('section-loaded');
        state.sectionsLoaded.add(index);
        return;
      }

      section.classList.add('section-loading');
    });

    // Force K/U videos to preload immediately — highest priority on page
    document.querySelectorAll('[id*="KnUnkn"] video, .ku-grid video').forEach(function(v) {
      v.preload = 'auto';
      v.muted = true;
      v.playsInline = true;
      v.load();
      v.play().catch(function(){});
    });

    // Store kuIndex for use in section reveal timeout
    state.kuIndex = kuIndex;

    // Add poster to slideshow videos that are missing one
    // Prevents black screen on slow connections (video takes 24-27s to buffer)
    // Note: slideshow.liquid now sets poster via asset_url for videos without a slide image,
    // so this JS fallback is a safety net for edge cases only
    document.querySelectorAll('.section-slideshow video.slide-video').forEach(function(v) {
      if (!v.poster && !v.getAttribute('poster')) {
        // Use the nearest slideshow image as poster, or a 1x1 #141414 pixel to match background
        var nearbyImg = v.closest('.shopify-section').querySelector('.slideshow--media img');
        if (nearbyImg && nearbyImg.src) {
          v.poster = nearbyImg.src;
        } else {
          // Transparent poster matching #141414 background — avoids browser default black
          v.poster = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"><rect fill="#141414" width="1" height="1"/></svg>');
        }
      }
    });

    // Start hero loading
    loadHero(sections[0]);

    // Set up intersection observer for scroll-triggered reveals
    setupIntersectionObserver(sections);

    // Defer below-fold videos before setting up autoplay (prevents 15+ concurrent loads)
    deferBelowFoldVideos();

    // Set up video autoplay handlers
    setupVideoAutoplay();

    // Fallback: ensure content visible after timeout
    setTimeout(forceRevealAll, CONFIG.heroLoadTimeout + 2000);
  }

  /**
   * Get all main content sections
   */
  function getSections() {
    const mainContent = document.querySelector('.content-for-layout');
    if (!mainContent) return [];

    return Array.from(mainContent.querySelectorAll(':scope > .shopify-section'));
  }

  /**
   * Load hero section first
   */
  function loadHero(heroSection) {
    if (!heroSection) {
      heroReady();
      return;
    }

    const heroMedia = getMediaElements(heroSection);

    if (heroMedia.length === 0) {
      // No media, reveal immediately
      revealSection(heroSection);
      heroReady();
      return;
    }

    // Wait for hero media to load
    Promise.race([
      waitForMedia(heroMedia),
      timeout(CONFIG.heroLoadTimeout)
    ]).then(() => {
      revealSection(heroSection);
      heroReady();
    });
  }

  /**
   * Hero is ready - enable scrolling
   */
  function heroReady() {
    state.heroReady = true;
    document.body.classList.add('loading-hero-ready');
    document.body.classList.remove('loading-active');

    // Start processing queued sections
    processRevealQueue();
  }

  /**
   * Get media elements (images, videos) from section
   */
  function getMediaElements(section) {
    const images = Array.from(section.querySelectorAll('img[src], img[srcset]'));
    const videos = Array.from(section.querySelectorAll('video'));
    return [...images, ...videos];
  }

  /**
   * Wait for media elements to load
   */
  function waitForMedia(mediaElements) {
    const promises = mediaElements.map(media => {
      if (media.tagName === 'IMG') {
        return waitForImage(media);
      } else if (media.tagName === 'VIDEO') {
        return waitForVideo(media);
      }
      return Promise.resolve();
    });

    return Promise.all(promises);
  }

  /**
   * Wait for image to load
   */
  function waitForImage(img) {
    return new Promise(resolve => {
      if (img.complete && img.naturalHeight > 0) {
        resolve();
        return;
      }

      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  /**
   * Wait for video to be ready to play
   */
  function waitForVideo(video) {
    return new Promise(resolve => {
      // Video is ready if it has enough data
      if (video.readyState >= 3) {
        resolve();
        return;
      }

      // Wait for canplay event
      video.addEventListener('canplay', resolve, { once: true });
      video.addEventListener('loadeddata', resolve, { once: true });
      video.addEventListener('error', resolve, { once: true });

      // If video has poster, consider it ready faster
      if (video.poster) {
        const posterImg = new Image();
        posterImg.onload = resolve;
        posterImg.src = video.poster;
      }
    });
  }

  /**
   * Create timeout promise
   */
  function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Set up intersection observer for scroll-triggered reveals
   */
  function setupIntersectionObserver(sections) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const section = entry.target;
        const index = parseInt(section.dataset.loadingIndex, 10);

        if (entry.isIntersecting) {
          state.sectionsInView.add(index);
          section.classList.add('section-in-view');
          queueSectionReveal(section, index);
        }
      });
    }, {
      root: null,
      rootMargin: '50px 0px',
      threshold: CONFIG.scrollThreshold
    });

    // Skip hero (index 0), observe rest
    sections.slice(1).forEach(section => observer.observe(section));
  }

  /**
   * Wait for K/U viewport videos to reach canplay state
   */
  function waitForKuVideos() {
    var kuVideos = document.querySelectorAll('[id*="KnUnkn"] video, .ku-grid video');
    if (kuVideos.length === 0) return Promise.resolve();

    var promises = [];
    kuVideos.forEach(function(v) {
      if (v.readyState >= 3) return; // already ready
      promises.push(new Promise(function(resolve) {
        v.addEventListener('canplay', resolve, { once: true });
        v.addEventListener('error', resolve, { once: true });
      }));
    });

    if (promises.length === 0) return Promise.resolve();

    // Wait for K/U videos but cap at 5s to avoid infinite blocking
    return Promise.race([
      Promise.all(promises),
      timeout(5000)
    ]);
  }

  /**
   * Queue section for reveal
   */
  function queueSectionReveal(section, index) {
    if (state.sectionsLoaded.has(index)) return;

    // Load section media first
    const media = getMediaElements(section);

    // Sections below K/U wait longer — ensures K/U videos play before lower content appears
    const isAfterKu = state.kuIndex !== undefined && index > state.kuIndex;
    const sectionTimeout = isAfterKu ? CONFIG.belowKuSectionLoadTimeout : CONFIG.sectionLoadTimeout;

    // If after K/U, also wait for K/U videos to reach canplay before revealing
    const kuGate = isAfterKu ? waitForKuVideos() : Promise.resolve();

    Promise.all([
      kuGate,
      Promise.race([
        waitForMedia(media),
        timeout(sectionTimeout)
      ])
    ]).then(() => {
      state.revealQueue.push({ section, index });

      // Sort queue by index to maintain order
      state.revealQueue.sort((a, b) => a.index - b.index);

      processRevealQueue();
    });
  }

  /**
   * Process reveal queue sequentially
   */
  function processRevealQueue() {
    if (!state.heroReady || state.isProcessingQueue) return;
    if (state.revealQueue.length === 0) return;

    state.isProcessingQueue = true;

    const next = state.revealQueue.shift();

    // Check if previous sections are loaded (maintain order)
    const previousLoaded = canRevealSection(next.index);

    if (previousLoaded) {
      revealSection(next.section);
      state.sectionsLoaded.add(next.index);

      // Continue processing after delay
      setTimeout(() => {
        state.isProcessingQueue = false;
        processRevealQueue();
      }, CONFIG.revealDelay);
    } else {
      // Put back in queue and wait
      state.revealQueue.unshift(next);
      state.isProcessingQueue = false;

      // Try again shortly
      setTimeout(processRevealQueue, 100);
    }
  }

  /**
   * Check if section can be revealed (previous sections loaded)
   */
  function canRevealSection(index) {
    // Hero (0) can always reveal
    if (index <= 1) return true;

    // Require previous section to be fully loaded (strict top-to-bottom order)
    return state.sectionsLoaded.has(index - 1);
  }

  /**
   * Reveal a section — simply swap classes, CSS handles the transition
   */
  function revealSection(section) {
    section.classList.remove('section-loading');
    section.classList.add('section-loaded');
  }

  /**
   * Defer below-fold videos: strip sources so they don't load until near viewport.
   * Must run BEFORE setupVideoAutoplay() to prevent immediate play attempts.
   */
  function deferBelowFoldVideos() {
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    document.querySelectorAll('video').forEach(function(video) {
      // Skip video journey videos — managed by hp-video-journey.liquid
      if (video.closest('#exp-master-container')) return;

      // Skip K/U grid videos — they must load immediately (visible on page load)
      if (video.closest('[id*="KnUnkn"]') || video.closest('.ku-grid')) return;

      var rect = video.getBoundingClientRect();
      // If video top is within 1.5x viewport height, consider it "above fold" — let it load
      if (rect.top < viewportHeight * 1.5) return;

      // Below fold: strip sources to prevent loading
      video.querySelectorAll('source').forEach(function(source) {
        if (source.src && !source.dataset.lazySrc) {
          source.dataset.lazySrc = source.src;
          source.removeAttribute('src');
        }
      });
      // Also handle direct src on video element
      if (video.src && !video.dataset.lazySrc) {
        video.dataset.lazySrc = video.src;
        video.removeAttribute('src');
      }
      video.preload = 'none';
      video.dataset.lazyDeferred = 'true';
    });

    // Single IntersectionObserver to restore sources when near viewport
    var lazyObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        // Don't restore deferred videos while journey video is playing
        if (document.body.classList.contains('journey-active')) return;
        var video = entry.target.querySelector ? entry.target.querySelector('video[data-lazy-deferred]') : null;
        if (!video && entry.target.tagName === 'VIDEO') video = entry.target;
        if (!video || !video.dataset.lazyDeferred) return;

        // Restore sources
        video.querySelectorAll('source[data-lazy-src]').forEach(function(source) {
          source.src = source.dataset.lazySrc;
          delete source.dataset.lazySrc;
        });
        if (video.dataset.lazySrc) {
          video.src = video.dataset.lazySrc;
          delete video.dataset.lazySrc;
        }
        delete video.dataset.lazyDeferred;
        video.load();
        if (video.autoplay || video.hasAttribute('autoplay')) {
          video.play().catch(function() {});
        }
        lazyObserver.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });

    // Observe the parent section of each deferred video (more reliable than observing video directly)
    document.querySelectorAll('video[data-lazy-deferred]').forEach(function(video) {
      var section = video.closest('.shopify-section') || video.parentElement;
      lazyObserver.observe(section);
    });

    // Strip autoplay from results slider videos so they don't compete for bandwidth
    // Swiper may force-init slides which triggers autoplay — remove it at source
    document.querySelectorAll('.section-results-slider video, [id*="RsSldr"] video').forEach(function(video) {
      if (video.dataset.lazyDeferred) return; // already handled above
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.preload = 'none';
      // Store sources for lazy restore
      video.querySelectorAll('source').forEach(function(source) {
        if (source.src && !source.dataset.lazySrc) {
          source.dataset.lazySrc = source.src;
          source.removeAttribute('src');
        }
      });
      if (video.src && !video.dataset.lazySrc) {
        video.dataset.lazySrc = video.src;
        video.removeAttribute('src');
      }
      video.dataset.lazyDeferred = 'true';
      // Observe for lazy loading
      var section = video.closest('.shopify-section') || video.parentElement;
      lazyObserver.observe(section);
    });

    // Ensure journey videos have at least metadata ready for quick playback
    var journeyVideos = document.querySelectorAll('#exp-master-container video');
    journeyVideos.forEach(function(video) {
      if (video.preload === 'none') {
        video.preload = 'metadata';
      }
    });

    // Sequential loading: after BOTH K/U videos fire canplay, allow preview section videos to load
    // Preview/reaction videos between K/U and results slider get loaded next
    waitForKuVideos().then(function() {
      // Find videos in sections between K/U and results slider
      var allSections = Array.from(document.querySelectorAll('.content-for-layout > .shopify-section'));
      var kuIdx = -1, rsIdx = -1;
      allSections.forEach(function(s, i) {
        if (s.id && s.id.indexOf('KnUnkn') !== -1) kuIdx = i;
        if (s.id && (s.id.indexOf('RsSldr') !== -1 || s.querySelector('.section-results-slider'))) rsIdx = i;
      });
      if (kuIdx >= 0) {
        var endIdx = rsIdx > kuIdx ? rsIdx : allSections.length;
        for (var i = kuIdx + 1; i < endIdx; i++) {
          allSections[i].querySelectorAll('video[data-lazy-deferred]').forEach(function(video) {
            video.querySelectorAll('source[data-lazy-src]').forEach(function(source) {
              source.src = source.dataset.lazySrc;
              delete source.dataset.lazySrc;
            });
            if (video.dataset.lazySrc) {
              video.src = video.dataset.lazySrc;
              delete video.dataset.lazySrc;
            }
            delete video.dataset.lazyDeferred;
            video.preload = 'auto';
            video.load();
            if (video.hasAttribute('autoplay')) {
              video.play().catch(function() {});
            }
          });
        }
      }
    });
  }

  /**
   * Set up video autoplay handlers
   */
  function setupVideoAutoplay() {
    // Find all videos that should autoplay
    const videos = document.querySelectorAll('video[autoplay], .slide-video');

    videos.forEach(video => {
      // Skip video journey videos — they have their own playback management
      if (video.closest('#exp-master-container')) return;
      // Skip K/U grid videos — they have their own IntersectionObserver in hp-known-unknown.liquid
      if (video.closest('[id*="KnUnkn"]') || video.closest('.ku-grid')) return;
      // Skip deferred below-fold videos — they'll be handled by lazy observer
      if (video.dataset.lazyDeferred) return;

      // Ensure video attributes
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      // Try to play when visible
      const playVideo = () => {
        if (video.paused) {
          video.play().catch(() => {
            // Autoplay blocked, that's ok
          });
        }
      };

      // Play when video can play
      video.addEventListener('canplay', playVideo);
      video.addEventListener('loadeddata', playVideo);

      // Play when section becomes visible
      const section = video.closest('.shopify-section');
      if (section) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              playVideo();
            }
          });
        }, { threshold: 0.1 });

        observer.observe(section);
      }

      // Try immediate play
      playVideo();
    });

    // Hide play buttons on autoplay containers
    document.querySelectorAll('.autoplay-true, .section-slideshow').forEach(container => {
      const playButtons = container.querySelectorAll('.video-play-button, [data-play-button], .deferred-media__poster-button');
      playButtons.forEach(btn => {
        btn.style.display = 'none';
        btn.style.opacity = '0';
        btn.style.pointerEvents = 'none';
      });
    });

    // Hide play button overlays on any video not yet ready to play
    document.querySelectorAll('.section-loaded video').forEach(function(vid) {
      if (vid.readyState < 2) {
        var parent = vid.parentElement;
        if (parent) {
          var playBtn = parent.querySelector('.video-play-button, .deferred-media__poster-button, [data-play-button]');
          if (playBtn) playBtn.style.display = 'none';
        }
      }
    });
  }

  /**
   * Force reveal all sections (fallback)
   */
  function forceRevealAll() {
    document.body.classList.remove('loading-active');
    document.body.classList.add('loading-hero-ready', 'loading-complete');

    document.querySelectorAll('.section-loading').forEach(section => {
      revealSection(section);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also initialize after full load for any late content
  window.addEventListener('load', () => {
    // Give a moment for any async content
    setTimeout(processRevealQueue, 500);
  });

})();

// Respect prefers-reduced-motion: pause all autoplay videos
(function() {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function handleMotionPreference(mq) {
    var videos = document.querySelectorAll('video[autoplay]');
    videos.forEach(function(video) {
      if (mq.matches) {
        video.pause();
        video.removeAttribute('autoplay');
        video.dataset.autoplayDisabled = 'reduced-motion';
      } else if (video.dataset.autoplayDisabled === 'reduced-motion') {
        video.setAttribute('autoplay', '');
        delete video.dataset.autoplayDisabled;
        video.play().catch(function() {});
      }
    });
  }

  if (prefersReducedMotion.addEventListener) {
    prefersReducedMotion.addEventListener('change', handleMotionPreference);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      handleMotionPreference(prefersReducedMotion);
    });
  } else {
    handleMotionPreference(prefersReducedMotion);
  }
})();
